/**
 * Google Analytics 4 utility for TraderTekno user platform.
 * GA4 Measurement ID configured in index.html (G-6C96E702T5).
 *
 * Usage:
 *   import { trackEvent, trackPageView } from '@/utils/ga4';
 *   trackPageView('/dashboard');
 *   trackEvent('sign_up', { method: 'google' });
 */

const GA_ID = 'G-6C96E702T5';

// ---------------------------------------------------------------------------
// Bot detection — silently drop all GA4 events for bots/crawlers
// ---------------------------------------------------------------------------

const BOT_PATTERNS = [
  /bot/i, /crawl/i, /spider/i, /slurp/i, /mediapartners/i,
  /googlebot/i, /bingbot/i, /yandex/i, /baidu/i, /duckduckbot/i,
  /facebookexternalhit/i, /twitterbot/i, /linkedinbot/i,
  /whatsapp/i, /telegrambot/i, /discordbot/i,
  /semrush/i, /ahrefs/i, /mj12bot/i, /dotbot/i,
  /headlesschrome/i, /phantom/i, /selenium/i, /puppeteer/i,
  /lighthouse/i, /pagespeed/i, /gtmetrix/i,
];

const isBot = typeof navigator !== 'undefined' &&
  BOT_PATTERNS.some((p) => p.test(navigator.userAgent));

// ---------------------------------------------------------------------------
// Internal traffic detection — developers set localStorage tt_internal=1
// All events still fire but carry traffic_type:'internal' (set in index.html).
// GA4 Admin data filter can then exclude traffic_type=internal from reports.
// ---------------------------------------------------------------------------

/** Check if current session is flagged as internal/developer traffic */
export function isInternalTraffic(): boolean {
  try { return localStorage.getItem('tt_internal') === '1'; } catch { return false; }
}

/** Flag current browser as internal traffic (run once in DevTools console) */
export function markAsInternal() {
  try { localStorage.setItem('tt_internal', '1'); } catch { /* ignore */ }
  // Re-apply traffic_type to active GA4 config
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_ID, { traffic_type: 'internal' });
  }
}

/** Remove internal traffic flag */
export function unmarkInternal() {
  try { localStorage.removeItem('tt_internal'); } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Core helpers
// ---------------------------------------------------------------------------

function gtag(...args: [string, ...unknown[]]) {
  if (isBot) return;
  if (typeof window !== 'undefined' && window.gtag) {
    (window.gtag as (...a: unknown[]) => void)(...args);
  }
}

/** Track a page view (call on route change in SPA) */
export function trackPageView(path: string, title?: string) {
  gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: title || document.title,
  });
}

/** Track a custom event */
export function trackEvent(
  eventName: string,
  params?: Record<string, unknown>,
) {
  gtag('event', eventName, params);
}

/** Set user properties (call after login) */
export function setUserProperties(props: { userId?: string; tier?: string }) {
  if (props.userId) {
    gtag('config', GA_ID, { user_id: props.userId });
  }
  if (props.tier) {
    gtag('set', 'user_properties', { subscription_tier: props.tier });
  }
}

/** Set A/B test variant as global user property so every event carries it */
export function setABVariant(variantId: string) {
  gtag('set', 'user_properties', { ab_test_variant: variantId });
}

// ---------------------------------------------------------------------------
// Onboarding funnel
// ---------------------------------------------------------------------------

export function trackOnboardingStep(step: number, totalSteps: number) {
  trackEvent('onboarding_step', {
    step,
    total_steps: totalSteps,
    completion_pct: Math.round((step / totalSteps) * 100),
  });
}

export function trackOnboardingComplete(selectedStocks: number) {
  trackEvent('onboarding_complete', { selected_stocks: selectedStocks });
}

export function trackOnboardingDrop(lastStep: number, totalSteps: number) {
  trackEvent('onboarding_drop', { last_step: lastStep, total_steps: totalSteps });
}

// ---------------------------------------------------------------------------
// Signal & analysis tracking
// ---------------------------------------------------------------------------

export function trackSignalDetailView(symbol: string, direction: string, confidence: number) {
  trackEvent('signal_detail_view', { symbol, direction, confidence });
}

/** Call on unmount to record how long user spent on signal detail */
export function trackSignalViewDuration(symbol: string, durationSec: number) {
  if (durationSec < 1) return;
  trackEvent('signal_view_duration', {
    symbol,
    duration_sec: Math.round(durationSec),
  });
}

export function trackSignalScrollDepth(depth: number) {
  // depth: 25, 50, 75, 100
  trackEvent('signal_scroll_depth', { depth_pct: depth });
}

export function trackAnalysisRead(symbol: string, section: string) {
  trackEvent('analysis_read', { symbol, section });
}

// ---------------------------------------------------------------------------
// PWA install
// ---------------------------------------------------------------------------

let deferredInstallPrompt: Event | null = null;
const PWA_DISMISS_KEY = 'tt_pwa_dismiss';
const PWA_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

export function initPWAInstallTracking() {
  window.addEventListener('beforeinstallprompt', (e) => {
    deferredInstallPrompt = e;

    try {
      const dismissed = localStorage.getItem(PWA_DISMISS_KEY);
      if (dismissed && Date.now() - Number(dismissed) < PWA_COOLDOWN_MS) return;
    } catch { /* ignore */ }

    trackEvent('pwa_install_prompt_shown');
  });

  window.addEventListener('appinstalled', () => {
    trackEvent('pwa_install_accepted');
    deferredInstallPrompt = null;
  });
}

export function getPWAInstallPrompt() {
  return deferredInstallPrompt;
}

export function dismissPWAInstallPrompt() {
  try {
    localStorage.setItem(PWA_DISMISS_KEY, String(Date.now()));
  } catch { /* ignore */ }
  deferredInstallPrompt = null;
}

// ---------------------------------------------------------------------------
// Payment & checkout
// ---------------------------------------------------------------------------

const CHECKOUT_KEY = 'tt_checkout_started';

export function trackCheckoutStarted(planId: string, amount: number) {
  localStorage.setItem(CHECKOUT_KEY, JSON.stringify({ planId, amount, ts: Date.now() }));
  trackEvent('begin_checkout', {
    currency: 'IDR',
    value: amount,
    item_id: planId,
  });
}

/** Call on app mount — detect abandoned checkouts (started > 30 min ago, no success) */
export function detectPaymentAbandoned() {
  try {
    const raw = localStorage.getItem(CHECKOUT_KEY);
    if (!raw) return;
    const { planId, amount, ts } = JSON.parse(raw);
    const ageMin = (Date.now() - ts) / 60_000;
    if (ageMin > 30) {
      // Only track if within 7 days — older entries are stale noise
      if (ageMin < 10_080) {
        trackEvent('payment_abandoned', {
          plan_id: planId,
          amount,
          abandoned_after_min: Math.round(ageMin),
        });
      }
      localStorage.removeItem(CHECKOUT_KEY);
    }
  } catch { /* ignore */ }
}

export function clearCheckoutState() {
  localStorage.removeItem(CHECKOUT_KEY);
}

// ---------------------------------------------------------------------------
// Session frequency & retention
// ---------------------------------------------------------------------------

const SESSION_KEY = 'tt_last_session';
const FIRST_VALUE_KEY = 'tt_first_value';

export function trackSessionFrequency() {
  try {
    const now = Date.now();
    const last = localStorage.getItem(SESSION_KEY);
    if (last) {
      const daysSince = Math.round((now - Number(last)) / 86_400_000 * 10) / 10;
      trackEvent('session_return', { days_since_last: daysSince });
    } else {
      trackEvent('first_session');
    }
    localStorage.setItem(SESSION_KEY, String(now));
  } catch { /* ignore */ }
}

export function trackFirstValueMoment(action: string) {
  try {
    if (localStorage.getItem(FIRST_VALUE_KEY)) return;
    localStorage.setItem(FIRST_VALUE_KEY, String(Date.now()));
    trackEvent('first_value_moment', { action });
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Notification permission
// ---------------------------------------------------------------------------

export function trackNotificationPermission(result: 'granted' | 'denied' | 'dismissed' | 'default') {
  trackEvent('notification_permission', { result });
}

// ---------------------------------------------------------------------------
// News & performa engagement
// ---------------------------------------------------------------------------

export function trackNewsRead(articleId: string, title: string) {
  trackEvent('news_read', { article_id: articleId, title: title.slice(0, 100) });
}

export function trackPerformaEngagement(section: string) {
  trackEvent('performa_engagement', { section });
}

// ---------------------------------------------------------------------------
// API error tracking (per user)
// ---------------------------------------------------------------------------

export function trackAPIError(endpoint: string, status: number, message: string) {
  trackEvent('api_error', {
    endpoint: endpoint.slice(0, 200),
    status,
    error_msg: message.slice(0, 100),
  });
}

// ---------------------------------------------------------------------------
// Core Web Vitals
// ---------------------------------------------------------------------------

export function trackCoreWebVitals() {
  if (typeof PerformanceObserver === 'undefined') return;

  // LCP
  try {
    const lcpObs = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
      if (last) trackEvent('web_vital_lcp', { value_ms: Math.round(last.startTime) });
      lcpObs.disconnect();
    });
    lcpObs.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch { /* unsupported */ }

  // FID
  try {
    const fidObs = new PerformanceObserver((list) => {
      const entry = list.getEntries()[0] as PerformanceEntry & { processingStart: number; startTime: number };
      if (entry) trackEvent('web_vital_fid', { value_ms: Math.round(entry.processingStart - entry.startTime) });
      fidObs.disconnect();
    });
    fidObs.observe({ type: 'first-input', buffered: true });
  } catch { /* unsupported */ }

  // CLS
  try {
    let clsValue = 0;
    const clsObs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as unknown as { hadRecentInput: boolean }).hadRecentInput) {
          clsValue += (entry as unknown as { value: number }).value;
        }
      }
    });
    clsObs.observe({ type: 'layout-shift', buffered: true });
    // Report CLS on page hide
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        trackEvent('web_vital_cls', { value: Math.round(clsValue * 1000) / 1000 });
        clsObs.disconnect();
      }
    }, { once: true });
  } catch { /* unsupported */ }
}

// ---------------------------------------------------------------------------
// Offline usage detection
// ---------------------------------------------------------------------------

export function initOfflineTracking() {
  window.addEventListener('online', () => {
    trackEvent('connectivity_change', { status: 'online' });
  });
  window.addEventListener('offline', () => {
    trackEvent('connectivity_change', { status: 'offline' });
  });
  if (!navigator.onLine) {
    trackEvent('app_opened_offline');
  }
}
